import React, {
  createContext,
  useContext,
  useReducer,
  useEffect,
  useCallback,
} from "react";
import { getProfileDetails } from "../utilities/ApiCalls";
const api = require("../../api/secureMessageApi");
const socketClient = require("../../api/secureSocket");

// Initial state
const initialState = {
  chats: [],
  selectedChatId: null,
  messages: [],
  currentUser: null,
  socketConnected: false,
  typingUsers: {},
  loading: {
    chats: true,
    messages: false,
  },
  error: null,
};

// Action types
const ACTIONS = {
  SET_LOADING: "SET_LOADING",
  SET_ERROR: "SET_ERROR",
  SET_CHATS: "SET_CHATS",
  SET_CHATS_COMPUTED: "SET_CHATS_COMPUTED",
  ADD_CHAT: "ADD_CHAT",
  UPDATE_CHAT: "UPDATE_CHAT",
  SELECT_CHAT: "SELECT_CHAT",
  SET_MESSAGES: "SET_MESSAGES",
  ADD_MESSAGE: "ADD_MESSAGE",
  UPDATE_MESSAGE: "UPDATE_MESSAGE",
  DELETE_MESSAGE: "DELETE_MESSAGE",
  SET_CURRENT_USER: "SET_CURRENT_USER",
  SET_SOCKET_CONNECTED: "SET_SOCKET_CONNECTED",
  SET_TYPING_USER: "SET_TYPING_USER",
  CLEAR_MESSAGES: "CLEAR_MESSAGES",
};

// Reducer
function secureChatReducer(state, action) {
  switch (action.type) {
    case ACTIONS.SET_LOADING:
      return {
        ...state,
        loading: {
          ...state.loading,
          [action.payload.key]: action.payload.value,
        },
      };

    case ACTIONS.SET_ERROR:
      return {
        ...state,
        error: action.payload,
      };

    case ACTIONS.SET_CHATS:
      return {
        ...state,
        chats: Array.isArray(action.payload) ? action.payload : [],
        loading: { ...state.loading, chats: false },
      };

    case ACTIONS.SET_CHATS_COMPUTED: {
      try {
        const prev = Array.isArray(state.chats) ? state.chats : [];
        const compute = action.payload;
        const next = typeof compute === "function" ? compute(prev) : prev;
        return {
          ...state,
          chats: Array.isArray(next) ? next : [],
          loading: { ...state.loading, chats: false },
        };
      } catch (_e) {
        return { ...state, loading: { ...state.loading, chats: false } };
      }
    }

    case ACTIONS.ADD_CHAT:
      return {
        ...state,
        chats: [action.payload, ...state.chats],
      };

    case ACTIONS.UPDATE_CHAT:
      return {
        ...state,
        chats: state.chats.map((chat) =>
          chat._id === action.payload._id
            ? { ...chat, ...action.payload }
            : chat
        ),
      };

    case ACTIONS.SELECT_CHAT:
      if (action.payload === state.selectedChatId) {
        // Ignore reselection of the same chat to preserve messages in view
        return state;
      }
      return {
        ...state,
        selectedChatId: action.payload,
        messages: [], // Clear messages when switching to a different chat
      };

    case ACTIONS.SET_MESSAGES:
      return {
        ...state,
        messages: action.payload,
        loading: { ...state.loading, messages: false },
      };

    case ACTIONS.ADD_MESSAGE:
      return {
        ...state,
        messages: [...state.messages, action.payload],
      };

    case ACTIONS.UPDATE_MESSAGE:
      return {
        ...state,
        messages: state.messages.map((msg) =>
          msg._id === action.payload._id ? { ...msg, ...action.payload } : msg
        ),
      };

    case ACTIONS.DELETE_MESSAGE:
      return {
        ...state,
        messages: state.messages.filter((msg) => msg._id !== action.payload),
      };

    case ACTIONS.SET_CURRENT_USER:
      return {
        ...state,
        currentUser: action.payload,
      };

    case ACTIONS.SET_SOCKET_CONNECTED:
      return {
        ...state,
        socketConnected: action.payload,
      };

    case ACTIONS.SET_TYPING_USER:
      return {
        ...state,
        typingUsers: {
          ...state.typingUsers,
          [action.payload.chatId]: {
            ...state.typingUsers[action.payload.chatId],
            [action.payload.userId]: action.payload.isTyping,
          },
        },
      };

    case ACTIONS.CLEAR_MESSAGES:
      return {
        ...state,
        messages: [],
      };

    default:
      return state;
  }
}

// Create context
const SecureChatContext = createContext();

// Custom hook to use context
export const useSecureChat = () => {
  const context = useContext(SecureChatContext);
  if (!context) {
    throw new Error("useSecureChat must be used within a SecureChatProvider");
  }
  return context;
};

// Context provider component
export const SecureChatProvider = ({ children }) => {
  const [state, dispatch] = useReducer(secureChatReducer, initialState);

  // Get current user from API
  const getCurrentUser = useCallback(async () => {
    // console.log("getCurrentUser");
    try {
      const token = localStorage.getItem("loginDetails");
      if (!token) {
        return null;
      }
      // console.log("token on getCurrentUser", token);
      const response = await getProfileDetails();
      // console.log("response on getCurrentUser", response);
      if (response && response.data.data && response.status !== "Failure") {
        return {
          _id: response.data.data._id,
          name: response.data.data.name || "User",
          email: response.data.data.email || "",
          profilePic: response.data.data.profilePic || "",
        };
      }
    } catch (error) {
      console.error("Error fetching user profile:", error);
    }
    return null;
  }, []);

  // Initialize user keys
  const initializeUserKeys = useCallback(async () => {
    try {
      dispatch({
        type: ACTIONS.SET_LOADING,
        payload: { key: "keys", value: true },
      });
      await api.initializeUserKeys();
      console.log("User keys initialized successfully");
    } catch (error) {
      console.warn("Key initialization warning:", error?.message || error);
      // Don't treat as error if keys already exist
    } finally {
      dispatch({
        type: ACTIONS.SET_LOADING,
        payload: { key: "keys", value: false },
      });
    }
  }, []);

  // Socket connection management
  const connectSocket = useCallback(() => {
    try {
      socketClient.connect();

      socketClient.socket?.on("connect", () => {
        console.log("Socket connected");
        dispatch({ type: ACTIONS.SET_SOCKET_CONNECTED, payload: true });
      });

      socketClient.socket?.on("disconnect", () => {
        console.log("Socket disconnected");
        dispatch({ type: ACTIONS.SET_SOCKET_CONNECTED, payload: false });
      });

      socketClient.socket?.on("connect_error", (error) => {
        console.error("Socket connection error:", error);
        dispatch({
          type: ACTIONS.SET_ERROR,
          payload: "Socket connection failed",
        });
      });
    } catch (error) {
      console.error("Error connecting socket:", error);
      dispatch({
        type: ACTIONS.SET_ERROR,
        payload: "Socket connection failed",
      });
    }
  }, []);

  // Disconnect socket
  const disconnectSocket = useCallback(() => {
    try {
      socketClient.disconnect();
      dispatch({ type: ACTIONS.SET_SOCKET_CONNECTED, payload: false });
    } catch (error) {
      console.error("Error disconnecting socket:", error);
    }
  }, []);

  // Action creators
  const selectChat = useCallback((chatId) => {
    dispatch({ type: ACTIONS.SELECT_CHAT, payload: chatId });
  }, []);

  const addMessage = useCallback((message) => {
    dispatch({ type: ACTIONS.ADD_MESSAGE, payload: message });
  }, []);

  const updateMessage = useCallback((messageId, updates) => {
    dispatch({
      type: ACTIONS.UPDATE_MESSAGE,
      payload: { _id: messageId, ...updates },
    });
  }, []);

  const deleteMessage = useCallback((messageId) => {
    dispatch({ type: ACTIONS.DELETE_MESSAGE, payload: messageId });
  }, []);

  const setTypingUser = useCallback((chatId, userId, isTyping) => {
    dispatch({
      type: ACTIONS.SET_TYPING_USER,
      payload: { chatId, userId, isTyping },
    });
  }, []);

  const addChat = useCallback((chat) => {
    dispatch({ type: ACTIONS.ADD_CHAT, payload: chat });
  }, []);

  const updateChat = useCallback((chatId, updates) => {
    dispatch({
      type: ACTIONS.UPDATE_CHAT,
      payload: { _id: chatId, ...updates },
    });
  }, []);

  const setChats = useCallback((next) => {
    if (typeof next === "function") {
      dispatch({ type: ACTIONS.SET_CHATS_COMPUTED, payload: next });
    } else {
      const safe = Array.isArray(next) ? next : [];
      try {
        console.log("SecureChatContext setChats:", {
          isArray: Array.isArray(next),
          storedLength: safe.length,
        });
      } catch (_) {}
      dispatch({ type: ACTIONS.SET_CHATS, payload: safe });
    }
  }, []);

  const setMessages = useCallback((messages) => {
    dispatch({ type: ACTIONS.SET_MESSAGES, payload: messages });
  }, []);

  const clearMessages = useCallback(() => {
    dispatch({ type: ACTIONS.CLEAR_MESSAGES });
  }, []);

  const setError = useCallback((error) => {
    dispatch({ type: ACTIONS.SET_ERROR, payload: error });
  }, []);

  const setLoading = useCallback((key, value) => {
    dispatch({ type: ACTIONS.SET_LOADING, payload: { key, value } });
  }, []);

  // Initialize on mount
  useEffect(() => {
    const initializeApp = async () => {
      try {
        const currentUser = await getCurrentUser();
        // console.log("currentUser on initializeApp", currentUser);
        if (currentUser) {
          dispatch({ type: ACTIONS.SET_CURRENT_USER, payload: currentUser });
          await initializeUserKeys();
          connectSocket();
        } else {
          dispatch({
            type: ACTIONS.SET_ERROR,
            payload: "User not authenticated",
          });
        }
      } catch (error) {
        console.error("Error initializing app:", error);
        dispatch({
          type: ACTIONS.SET_ERROR,
          payload: "Failed to initialize application",
        });
      }
    };

    initializeApp();

    // Cleanup on unmount
    return () => {
      disconnectSocket();
    };
  }, [getCurrentUser, initializeUserKeys, connectSocket, disconnectSocket]);

  // Context value
  const value = {
    // State
    ...state,

    // Actions
    selectChat,
    addMessage,
    updateMessage,
    deleteMessage,
    setTypingUser,
    addChat,
    updateChat,
    setChats,
    setMessages,
    clearMessages,
    setError,
    setLoading,

    // Socket
    connectSocket,
    disconnectSocket,
  };

  return (
    <SecureChatContext.Provider value={value}>
      {children}
    </SecureChatContext.Provider>
  );
};

export default SecureChatContext;
